import React, { useEffect, useState } from "react";
import {
  ncgeReviewQueue, ncgeApproveCampaign, ncgeRejectCampaign,
  ncgeMarkLaunched, ncgeRecordMetrics, ncgeGetSettings, ncgeSetReviewerEmail,
  fetchNcgeCampaign,
} from "../lib/api";

/**
 * AdminNCGE — Lanre/VA's review + launch console for NCGE campaigns.
 *
 * Tabs:
 *   • Pending Review — paid campaigns awaiting approval
 *   • Approved — approved but not yet launched in Meta
 *   • Launched — live in Meta; metrics input here
 *   • Settings — reviewer email + nightly digest config
 */
const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";
const GREY = "#5a5d66";
const BG = "#F8F9FA";

const TABS = [
  { key: "pending_review", label: "PENDING REVIEW" },
  { key: "approved", label: "APPROVED" },
  { key: "launched", label: "LAUNCHED" },
  { key: "rejected", label: "REJECTED" },
  { key: "settings", label: "SETTINGS" },
];

const AdminNCGE = () => {
  const [tab, setTab] = useState("pending_review");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (tab === "settings") {
      try { setSettings(await ncgeGetSettings()); } catch { /* ignore */ }
      return;
    }
    setLoading(true);
    try {
      const r = await ncgeReviewQueue(tab);
      setItems(r.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [tab]);

  const reload = async () => { await load(); };

  const onApprove = async (id) => {
    const email = window.prompt("Your reviewer email:", "lanre@nearnow.biz");
    if (!email) return;
    setBusy(true);
    try { await ncgeApproveCampaign(id, { approver_email: email }); await reload(); }
    catch (e) { alert("Approval failed: " + (e?.response?.data?.detail?.error || "unknown")); }
    finally { setBusy(false); }
  };

  const onReject = async (id) => {
    const reason = window.prompt("Reason for rejection (required):");
    if (!reason) return;
    const email = window.prompt("Your reviewer email:", "lanre@nearnow.biz");
    if (!email) return;
    setBusy(true);
    try { await ncgeRejectCampaign(id, { rejector_email: email, reason }); await reload(); }
    catch (e) { alert("Rejection failed"); }
    finally { setBusy(false); }
  };

  const onMarkLaunched = async (id) => {
    const metaId = window.prompt("Meta Ads Manager campaign ID (optional):") || "";
    setBusy(true);
    try { await ncgeMarkLaunched(id, { meta_campaign_id: metaId }); await reload(); }
    catch (e) { alert("Mark-launched failed"); }
    finally { setBusy(false); }
  };

  const onRecordMetrics = async (id) => {
    const metrics = {};
    const fields = [
      ["impressions", "Impressions"], ["clicks", "Clicks"],
      ["ctr_pct", "CTR %"], ["cpc_usd", "CPC ($)"],
      ["leads", "Leads"], ["cpl_usd", "Cost / Lead ($)"],
      ["appointments", "Appointments"], ["customers", "Customers"],
      ["spend_usd", "Spend ($)"], ["roi_x", "ROI (x)"],
    ];
    for (const [k, label] of fields) {
      const v = window.prompt(`${label} (leave blank to skip):`);
      if (v && v.trim() !== "") metrics[k] = Number(v);
    }
    if (!Object.keys(metrics).length) return;
    setBusy(true);
    try { await ncgeRecordMetrics(id, metrics); await reload(); }
    catch (e) { alert("Metrics save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-ncge-page" style={{ minHeight: "100vh", background: BG, padding: "28px 22px 80px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <p style={{ color: GOLD, letterSpacing: 4, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
          ADMIN · NCGE COMMAND
        </p>
        <h1 className="font-display" style={{ fontSize: 56, color: NAVY, letterSpacing: 1, margin: "6px 0 16px" }}>
          GROWTH ENGINE
        </h1>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => { setTab(t.key); setOpenId(null); }}
              className="tap"
              style={{
                background: tab === t.key ? NAVY : "transparent",
                color: tab === t.key ? GOLD : NAVY,
                border: `2px solid ${NAVY}`, padding: "10px 18px",
                fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
                borderRadius: 999, cursor: "pointer", fontSize: 13,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "settings" ? (
          <SettingsTab settings={settings} onSaved={async (e) => {
            await ncgeSetReviewerEmail(e); await load();
          }} />
        ) : (
          <QueueTab
            tab={tab}
            items={items} loading={loading}
            openId={openId} setOpenId={setOpenId}
            onApprove={onApprove} onReject={onReject}
            onMarkLaunched={onMarkLaunched}
            onRecordMetrics={onRecordMetrics}
            busy={busy} reload={reload}
          />
        )}
      </div>
    </div>
  );
};

const QueueTab = ({ tab, items, loading, openId, setOpenId, onApprove, onReject, onMarkLaunched, onRecordMetrics, busy, reload }) => (
  <div data-testid={`queue-${tab}`} style={{ background: "#fff", borderRadius: 12, padding: 0, boxShadow: "0 12px 30px rgba(10,22,40,0.06)" }}>
    <div style={{ padding: "14px 20px", borderBottom: `1px solid ${BG}`, display: "flex", justifyContent: "space-between" }}>
      <div className="font-display" style={{ fontSize: 22, color: NAVY, letterSpacing: 1 }}>
        {tab.replace(/_/g, " ").toUpperCase()} · {items.length}
      </div>
      <button data-testid="reload-btn" onClick={reload} className="btn-outline-navy tap" style={{ minHeight: 36, fontSize: 12 }}>↻ RELOAD</button>
    </div>

    {loading ? (
      <div style={{ padding: 30, textAlign: "center", color: GREY }}>Loading…</div>
    ) : items.length === 0 ? (
      <div style={{ padding: 36, textAlign: "center", color: GREY }}>
        No campaigns in {tab.replace(/_/g, " ")}.
      </div>
    ) : (
      <table data-testid="queue-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
        <thead>
          <tr style={{ background: BG }}>
            <Th>CONTRACTOR</Th><Th>CAMPAIGN</Th><Th align="right">PAID</Th>
            <Th align="right">METRICS</Th><Th align="right">ACTIONS</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => {
            const isOpen = openId === c.id;
            return (
              <React.Fragment key={c.id}>
                <tr style={{ borderTop: `1px solid ${BG}`, cursor: "pointer" }}
                    onClick={() => setOpenId(isOpen ? null : c.id)}
                    data-testid={`queue-row-${c.id}`}>
                  <td style={cell}>
                    <strong style={{ color: NAVY }}>{c.contractor?.business_name || c.contractor?.owner_name || "—"}</strong><br/>
                    <span style={{ fontSize: 12, color: GREY }}>
                      {c.contractor?.trade || "—"} · {c.contractor?.city || "—"}
                    </span>
                  </td>
                  <td style={cell}>{c.campaign_name || "Untitled"}<br/>
                    <span style={{ fontSize: 11, color: GREY }}>{c.id.slice(0, 8)}…</span>
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>{c.paid_at ? new Date(c.paid_at).toLocaleDateString() : "—"}</td>
                  <td style={{ ...cell, textAlign: "right", fontSize: 13 }}>
                    {c.metrics ? (
                      <span>{c.metrics.leads ?? "—"} leads · {c.metrics.roi_x ? `${c.metrics.roi_x}× ROI` : ""}</span>
                    ) : "—"}
                  </td>
                  <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }}>
                    {tab === "pending_review" && (
                      <>
                        <button data-testid={`approve-${c.id}`} onClick={(e) => { e.stopPropagation(); onApprove(c.id); }} disabled={busy}
                          className="btn-gold tap" style={{ minHeight: 36, fontSize: 12, padding: "0 12px" }}>
                          APPROVE
                        </button>
                        <button data-testid={`reject-${c.id}`} onClick={(e) => { e.stopPropagation(); onReject(c.id); }} disabled={busy}
                          className="btn-outline-navy tap" style={{ minHeight: 36, fontSize: 12, padding: "0 10px", marginLeft: 6, color: RED, borderColor: RED }}>
                          REJECT
                        </button>
                      </>
                    )}
                    {tab === "approved" && (
                      <button data-testid={`launched-${c.id}`} onClick={(e) => { e.stopPropagation(); onMarkLaunched(c.id); }} disabled={busy}
                        className="btn-gold tap" style={{ minHeight: 36, fontSize: 12, padding: "0 12px" }}>
                        ✓ LAUNCHED IN META
                      </button>
                    )}
                    {tab === "launched" && (
                      <button data-testid={`metrics-${c.id}`} onClick={(e) => { e.stopPropagation(); onRecordMetrics(c.id); }} disabled={busy}
                        className="btn-outline-navy tap" style={{ minHeight: 36, fontSize: 12, padding: "0 12px" }}>
                        + METRICS
                      </button>
                    )}
                  </td>
                </tr>
                {isOpen && (
                  <tr data-testid={`brief-${c.id}`}>
                    <td colSpan={5} style={{ background: BG, padding: 18 }}>
                      <CampaignBrief c={c} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    )}
  </div>
);

const CampaignBrief = ({ c }) => {
  const brief = c.creative_brief || {};
  const ads = brief.ads || [];
  return (
    <div>
      {c.lds_snapshot && (
        <div style={{ background: "#fff", padding: 12, borderRadius: 8, marginBottom: 12 }}>
          <strong style={{ color: NAVY }}>LDS: {c.lds_snapshot.overall} / 5 — {c.lds_snapshot.tier_label}</strong><br/>
          <span style={{ fontSize: 13, color: GREY }}>{c.lds_snapshot.recommended_ad_angle}</span>
        </div>
      )}
      {brief.target_audience && (
        <div style={{ background: "#fff", padding: 12, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>
          <strong>TARGET:</strong> {brief.target_audience.city}, {brief.target_audience.radius_miles}mi · age {brief.target_audience.age_range} · income $\u2265{brief.target_audience.household_income_min?.toLocaleString()}<br/>
          <strong>INTERESTS:</strong> {(brief.target_audience.interests || []).join(", ")}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
        {ads.map((a, i) => (
          <div key={i} data-testid={`ad-card-${i}`} style={{ background: "#fff", padding: 14, borderRadius: 8, border: `1px solid ${BG}` }}>
            <div style={{ color: GOLD, fontSize: 11, letterSpacing: 2, fontFamily: "Bebas Neue, sans-serif" }}>
              {a.objective || "Ad"} · {a.format || ""}
            </div>
            <h4 style={{ margin: "6px 0", color: NAVY, fontSize: 16 }}>{a.headline}</h4>
            <p style={{ margin: "0 0 6px", fontSize: 13, color: NAVY }}>{a.primary_text}</p>
            {a.description && <p style={{ margin: "0 0 6px", fontSize: 12, color: GREY }}>{a.description}</p>}
            <span style={{ display: "inline-block", padding: "3px 8px", background: NAVY, color: GOLD,
              borderRadius: 4, fontSize: 11, letterSpacing: 1.5 }}>
              {a.cta_button}
            </span>
          </div>
        ))}
      </div>
      {c.rejected_reason && (
        <div style={{ marginTop: 12, padding: 10, background: "#fde7e7", color: RED, borderRadius: 6 }}>
          REJECTED: {c.rejected_reason}
        </div>
      )}
    </div>
  );
};

const SettingsTab = ({ settings, onSaved }) => {
  const [email, setEmail] = useState("");
  useEffect(() => { setEmail(settings?.reviewer_email || ""); }, [settings]);
  if (!settings) return <div style={{ padding: 30, color: GREY }}>Loading…</div>;
  return (
    <div data-testid="settings-tab" style={{ background: "#fff", padding: 26, borderRadius: 12, boxShadow: "0 12px 30px rgba(10,22,40,0.06)" }}>
      <h3 style={{ margin: 0, color: NAVY }}>Reviewer email</h3>
      <p style={{ fontSize: 13, color: GREY, marginTop: 4 }}>
        Whoever you assign here receives the nightly 9 PM ET digest of campaigns awaiting approval. Set it to your VA or employee.
      </p>
      <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
        <input data-testid="reviewer-email-input" value={email} onChange={(e) => setEmail(e.target.value)} style={inp} placeholder="reviewer@nearnow.biz" />
        <button data-testid="save-reviewer-btn" onClick={() => onSaved(email)} className="btn-gold tap" style={{ minHeight: 48, paddingLeft: 22, paddingRight: 22 }}>SAVE</button>
      </div>
      <div style={{ marginTop: 22, padding: 14, background: BG, borderRadius: 8, fontSize: 13, color: GREY }}>
        <strong style={{ color: NAVY }}>Pricing config:</strong> ${settings.min_budget_cents / 100} per campaign · ${settings.service_fee_cents / 100} NearNow fee · ${(settings.min_budget_cents - settings.service_fee_cents) / 100} ad spend.
      </div>
    </div>
  );
};

const Th = ({ children, align }) => (
  <th style={{ textAlign: align || "left", padding: "12px 14px", fontSize: 11,
    color: GREY, letterSpacing: 1.5, fontFamily: "Bebas Neue, sans-serif" }}>{children}</th>
);
const cell = { padding: "14px" };
const inp = {
  flex: 1, minHeight: 48, fontSize: 15, padding: "0 14px",
  border: `2px solid #cbd0db`, borderRadius: 8, background: "#fff", outline: "none", boxSizing: "border-box",
};

export default AdminNCGE;
